import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, Camera, Video, FlipHorizontal2, Square, Loader2, Image } from 'lucide-react';
import '../styles/CameraModal.css';

function CameraModal({ isOpen, onClose, onCapture, photoOnly = false, noVideo = false, frontOnly = false }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const currentStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const videoFragmentsRef = useRef([]);
  const sessionIdRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [items, setItems] = useState([]);
  const [facingMode, setFacingMode] = useState(frontOnly ? 'user' : 'environment');
  const [recording, setRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [switching, setSwitching] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Zoom
  const zoomCapRef = useRef(null);   // { min, max, step }
  const currentZoomRef = useRef(1);
  const pinchStartDistRef = useRef(null);
  const pinchStartZoomRef = useRef(1);
  const [zoomDisplay, setZoomDisplay] = useState(null); // { value } или null
  const zoomTimerRef = useRef(null);

  const addLog = (msg) => {
    console.log('[Camera]', msg);
  };

  const getVideoTrack = () => {
    if (!currentStreamRef.current) return null;
    const tracks = currentStreamRef.current.getVideoTracks();
    return tracks.length > 0 ? tracks[0] : null;
  };

  const initZoom = (stream) => {
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    const caps = track.getCapabilities ? track.getCapabilities() : {};
    if (caps.zoom) {
      zoomCapRef.current = { min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1 };
      currentZoomRef.current = 1;
      addLog(`Zoom supported: min=${caps.zoom.min} max=${caps.zoom.max}`);
    } else {
      zoomCapRef.current = null;
      addLog('Zoom not supported on this device/browser');
    }
  };

  const applyZoom = useCallback((newZoom) => {
    const cap = zoomCapRef.current;
    if (!cap) return;
    const track = getVideoTrack();
    if (!track) return;

    const clamped = Math.min(Math.max(newZoom, cap.min), cap.max);
    currentZoomRef.current = clamped;

    track.applyConstraints({ advanced: [{ zoom: clamped }] }).catch(() => {});

    // Показать индикатор
    setZoomDisplay(clamped.toFixed(1));
    if (zoomTimerRef.current) clearTimeout(zoomTimerRef.current);
    zoomTimerRef.current = setTimeout(() => setZoomDisplay(null), 1500);
  }, []);

  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      pinchStartDistRef.current = getDistance(e.touches);
      pinchStartZoomRef.current = currentZoomRef.current;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && pinchStartDistRef.current !== null) {
      e.preventDefault();
      const newDist = getDistance(e.touches);
      const ratio = newDist / pinchStartDistRef.current;
      applyZoom(pinchStartZoomRef.current * ratio);
    }
  }, [applyZoom]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) {
      pinchStartDistRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setItems(prev => {
        prev.forEach(item => URL.revokeObjectURL(item.preview));
        return [];
      });

      if (currentStreamRef.current) {
        currentStreamRef.current.getTracks().forEach(t => t.stop());
        currentStreamRef.current = null;
      }

      zoomCapRef.current = null;
      currentZoomRef.current = 1;
      setZoomDisplay(null);

      return;
    }

    const start = async () => {
      try {
        setReady(false);
        setError(null);
        setItems([]);

        recordingChunksRef.current = [];
        videoFragmentsRef.current = [];
        mediaRecorderRef.current = null;
        sessionIdRef.current = null;
        setRecordTime(0);
        setRecording(false);
        setSwitching(false);
        setUploadProgress(null);

        addLog('🎥 Camera opened, initializing...');

        if (videoRef.current?.srcObject) {
          videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        }

        let attempts = 0;
        while (!videoRef.current && attempts < 20) {
          await new Promise(r => setTimeout(r, 50));
          attempts++;
        }

        if (!videoRef.current) {
          throw new Error('Не удалось инициализировать камеру');
        }

        const mediaConstraints = {
          video: {
            facingMode: facingMode,
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
            frameRate: { ideal: 30 }
          },
        };
        if (!photoOnly) {
          mediaConstraints.audio = {
            sampleRate: 48000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true
          };
        }
        const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);

        currentStreamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        initZoom(stream);
        setReady(true);

      } catch (e) {
        setError(e.message);
      }
    };

    start();

    return () => {
      if (currentStreamRef.current) {
        currentStreamRef.current.getTracks().forEach(t => t.stop());
        currentStreamRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    let interval;
    if (recording) {
      interval = setInterval(() => {
        setRecordTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [recording]);

  const switchCamera = async () => {
    if (switching) return;

    try {
      setSwitching(true);
      const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';

      addLog(`=== SWITCH CAMERA START ===`);
      addLog(`From: ${facingMode}, To: ${newFacingMode}, Recording: ${recording}`);

      const wasRecording = recording;

      if (wasRecording && mediaRecorderRef.current) {
        await new Promise((resolve) => {
          const originalOnstop = mediaRecorderRef.current.onstop;
          mediaRecorderRef.current.onstop = () => {
            if (originalOnstop) originalOnstop();
            resolve();
          };
          mediaRecorderRef.current.stop();
        });

        setRecording(false);

        await new Promise(resolve => setTimeout(resolve, 50));
      }

      if (currentStreamRef.current) {
        currentStreamRef.current.getTracks().forEach(t => t.stop());
      }

      zoomCapRef.current = null;
      currentZoomRef.current = 1;
      setZoomDisplay(null);

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          frameRate: { ideal: 30 }
        },
        audio: {
          sampleRate: 48000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      currentStreamRef.current = newStream;
      videoRef.current.srcObject = newStream;
      setFacingMode(newFacingMode);
      initZoom(newStream);

      try {
        await videoRef.current.play();
      } catch (err) {
        addLog('Stream play warning: ' + err.message);
      }

      if (!wasRecording) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      if (wasRecording) {
        startRecording();
      }

      addLog('=== SWITCH CAMERA END ===');

    } catch (e) {
      console.error('Ошибка переключения камеры:', e);
      setError('Не удалось переключить камеру');
    } finally {
      setSwitching(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || video.videoWidth === 0) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      const file = new File([blob], `photo_${Date.now()}_${Math.random().toString(36).substr(2, 8)}.jpg`, { type: 'image/jpeg' });
      const preview = URL.createObjectURL(blob);
      setItems(prev => [...prev, { file, preview, type: 'image' }]);
    }, 'image/jpeg', 0.9);
  };

  const startRecording = () => {
    addLog(`=== START RECORDING ===`);

    if (processing) {
      addLog('✗ Cannot start: previous recording is processing');
      return;
    }

    const stream = currentStreamRef.current;
    if (!stream) {
      addLog('✗ No stream available');
      return;
    }

    if (!sessionIdRef.current) {
      recordingChunksRef.current = [];
      videoFragmentsRef.current = [];
      sessionIdRef.current = Date.now().toString(36) + Math.random().toString(36).substr(2);
      setRecordTime(0);
      addLog(`✓ NEW session initialized: ${sessionIdRef.current}`);
    } else {
      addLog(`✓ CONTINUING session: ${sessionIdRef.current}`);
    }

    let options;
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
      options = { mimeType: 'video/webm;codecs=vp9,opus', videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 };
    } else if (MediaRecorder.isTypeSupported('video/webm;codecs=h264,opus')) {
      options = { mimeType: 'video/webm;codecs=h264,opus', videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 };
    } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
      options = { mimeType: 'video/webm;codecs=vp8,opus', videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 };
    } else {
      options = { videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 };
    }

    const mediaRecorder = new MediaRecorder(stream, options);

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        recordingChunksRef.current.push(e.data);
      }
    };

    mediaRecorder.onstop = () => {
      if (recordingChunksRef.current.length > 0) {
        const fragmentBlob = new Blob(recordingChunksRef.current, { type: 'video/webm' });
        videoFragmentsRef.current.push(fragmentBlob);
        recordingChunksRef.current = [];
      }
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setRecording(true);
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && recording) {
      await new Promise((resolve) => {
        const originalOnstop = mediaRecorderRef.current.onstop;
        mediaRecorderRef.current.onstop = () => {
          if (originalOnstop) originalOnstop();
          resolve();
        };
        mediaRecorderRef.current.stop();
      });

      mediaRecorderRef.current = null;
      setRecording(false);

      setTimeout(async () => {
        setProcessing(true);
        const fragments = videoFragmentsRef.current;

        if (fragments.length === 0) return;

        try {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          let thumbnail = null;

          if (video && canvas && video.videoWidth > 0) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d').drawImage(video, 0, 0);
            thumbnail = canvas.toDataURL('image/jpeg', 0.7);
          }

          if (fragments.length === 1) {
            const file = new File([fragments[0]], `video_${Date.now()}_${Math.random().toString(36).substr(2, 8)}.webm`, { type: 'video/webm' });
            setItems(prev => [...prev, {
              file,
              preview: thumbnail || URL.createObjectURL(fragments[0]),
              type: 'video',
              isThumb: !!thumbnail
            }]);
          } else {
            const sessionId = sessionIdRef.current;
            const token = localStorage.getItem('token');

            for (let i = 0; i < fragments.length; i++) {
              setUploadProgress({ current: i + 1, total: fragments.length, stage: 'upload' });

              const formData = new FormData();
              formData.append('video', fragments[i]);
              formData.append('sessionId', sessionId);
              formData.append('index', i.toString());
              formData.append('total', fragments.length.toString());

              const uploadRes = await fetch('/api/video/upload-fragment', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
              });

              if (!uploadRes.ok) {
                const errorText = await uploadRes.text();
                throw new Error(`Upload failed: ${uploadRes.status} ${errorText}`);
              }
            }

            setUploadProgress({ current: fragments.length, total: fragments.length, stage: 'merge' });

            const mergeRes = await fetch('/api/video/merge', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ sessionId })
            });

            if (!mergeRes.ok) {
              const errorText = await mergeRes.text();
              throw new Error(`Merge failed: ${mergeRes.status} ${errorText}`);
            }

            const mergedBlob = await mergeRes.blob();
            const file = new File([mergedBlob], `video_${Date.now()}_${Math.random().toString(36).substr(2, 8)}.webm`, { type: 'video/webm' });

            setItems(prev => [...prev, {
              file,
              preview: thumbnail || URL.createObjectURL(mergedBlob),
              type: 'video',
              isThumb: !!thumbnail
            }]);

            setError(null);
            setUploadProgress(null);
          }
        } catch (err) {
          console.error('[Camera] Video processing error:', err);
          setError('Ошибка: ' + err.message);
          setUploadProgress(null);
        }

        videoFragmentsRef.current = [];
        recordingChunksRef.current = [];
        setRecordTime(0);
        setProcessing(false);
      }, 100);
    }
  };

  const removeItem = (index) => {
    setItems(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="camera-modal-overlay" onClick={onClose}>
      <div className="camera-modal" onClick={(e) => e.stopPropagation()}>
        <div className="camera-header">
          <button className="btn-close-camera" onClick={onClose}><X size={18} strokeWidth={2} /></button>
          <h3>Камера</h3>
          <div style={{width: '40px'}}></div>
        </div>

        <div className="camera-content">
          {recording && (
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(255,0,0,0.95)',
              color: 'white',
              padding: '8px 16px',
              fontSize: '16px',
              fontWeight: 'bold',
              zIndex: 1000,
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: 'white',
                animation: 'pulse 1.5s ease-in-out infinite'
              }}></span>
              {formatTime(recordTime)}
            </div>
          )}

          <div
            className="camera-preview"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="camera-video"
              style={{
                width:'100%',
                height:'100%',
                objectFit:'cover',
                display: ready ? 'block' : 'none',
                transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
              }}
            />
            <canvas ref={canvasRef} style={{display:'none'}} />

            {zoomDisplay && (
              <div className="camera-zoom-indicator">
                {zoomDisplay}×
              </div>
            )}

            {ready && (
              <div className="camera-overlay-controls">
                {!recording && !switching ? (
                  <>
                    <button
                      className="btn-overlay-control"
                      onClick={capturePhoto}
                      disabled={switching}
                      title="Фото"
                    >
                      <Camera size={24} strokeWidth={1.8} />
                    </button>
                    {!photoOnly && !noVideo && (
                      <button
                        className="btn-overlay-control btn-video"
                        onClick={startRecording}
                        disabled={switching}
                        title="Видео"
                      >
                        <Video size={24} strokeWidth={1.8} />
                      </button>
                    )}
                    {(!photoOnly || noVideo) && !frontOnly && (
                      <button
                        className="btn-overlay-control"
                        onClick={switchCamera}
                        disabled={switching}
                        title="Переключить"
                      >
                        <FlipHorizontal2 size={24} strokeWidth={1.8} />
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      className="btn-overlay-control"
                      onClick={switchCamera}
                      disabled={switching}
                      title="Переключить камеру"
                      style={{ marginRight: 'auto' }}
                    >
                      {switching ? <Loader2 size={24} strokeWidth={1.8} className="spin" /> : <FlipHorizontal2 size={24} strokeWidth={1.8} />}
                    </button>
                    <button
                      className="btn-overlay-control btn-stop"
                      onClick={stopRecording}
                      disabled={switching}
                      title="Остановить"
                    >
                      <Square size={22} strokeWidth={1.8} />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {!ready && !error && (
            <div className="camera-error">
              <p style={{fontSize:'48px'}}>⏳</p>
            </div>
          )}

          {error && (
            <div className="camera-error">
              <p style={{fontSize:'32px'}}>❌</p>
              <p style={{fontSize:'13px',padding:'0 20px',opacity:0.8}}>{error}</p>
            </div>
          )}

          {uploadProgress && (
            <div style={{
              position:'absolute',
              bottom:'20px',
              left:'20px',
              right:'20px',
              background:'rgba(0,0,0,0.9)',
              padding:'16px',
              borderRadius:'12px',
              zIndex:999
            }}>
              <div style={{
                color:'white',
                fontSize:'14px',
                marginBottom:'8px',
                textAlign:'center'
              }}>
                {uploadProgress.stage === 'upload'
                  ? `Загрузка ${uploadProgress.current}/${uploadProgress.total}...`
                  : 'Склейка видео...'}
              </div>
              <div style={{
                width:'100%',
                height:'4px',
                background:'rgba(255,255,255,0.2)',
                borderRadius:'2px',
                overflow:'hidden'
              }}>
                <div style={{
                  width: uploadProgress.stage === 'merge' ? '100%' : `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                  height:'100%',
                  background:'linear-gradient(90deg, #4CAF50, #8BC34A)',
                  transition:'width 0.3s ease',
                  animation: uploadProgress.stage === 'merge' ? 'pulse 1.5s ease-in-out infinite' : 'none'
                }}></div>
              </div>
            </div>
          )}

          {items.length > 0 && (
            <div className="captured-photos-preview">
              {items.map((item, i) => (
                <div key={i} className="captured-photo-item">
                  {item.type === 'image' || item.isThumb ? (
                    <img src={item.preview} alt={`${i+1}`} />
                  ) : (
                    <video
                      src={item.preview}
                      preload="metadata"
                      muted
                      playsInline
                      style={{width:'100%',height:'100%',objectFit:'cover'}}
                    />
                  )}
                  <div style={{
                    position:'absolute',
                    top:'4px',
                    left:'4px',
                    background:'rgba(0,0,0,0.7)',
                    color:'white',
                    padding:'2px 6px',
                    fontSize:'11px',
                    borderRadius:'3px'
                  }}>
                    {item.type === 'image' ? <Image size={12} strokeWidth={2} /> : <Video size={12} strokeWidth={2} />}
                  </div>
                  <button className="btn-remove-captured" onClick={() => removeItem(i)}><X size={12} strokeWidth={2.5} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="camera-controls">
            <button className="btn-done" onClick={() => { onCapture(items); onClose(); }}>
              Готово ({items.length})
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

export default CameraModal;
